import * as React from 'react'
import cn from 'classnames'
import type { PropsWithRequiredChildren } from '../../../types/helpers'
import {
  Button as BaseButton,
  ButtonOwnProps as BaseButtonProps,
} from '../../atoms/Button'
import styles from './Button.module.scss'
import type { PolymorphicComponentProps } from 'react-polymorphic-box'

type DefaultElement = 'button'

export type ButtonOwnProps = PropsWithRequiredChildren<
  Omit<BaseButtonProps, 'variant' | 'themed' | 'size'>
>

export type ButtonProps<E extends React.ElementType> =
  PolymorphicComponentProps<E, ButtonOwnProps>

// @types/react 18 can't verify a generic render function against
// forwardRef's non-generic ForwardRefRenderFunction signature; the trailing
// `as unknown as` restores the correct generic call signature for callers,
// same as it always has.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = React.forwardRef((<
  E extends React.ElementType = DefaultElement,
>(
  { children, className, ...restProps }: ButtonProps<E>,
  ref: typeof restProps.ref,
) => {
  return (
    <BaseButton
      className={cn(styles.button, className)}
      ref={ref}
      // TS 5 can't verify this spread while `E` is still an unresolved type
      // parameter: BaseButton's props and ButtonProps<E> both reduce to
      // `Omit<ComponentProps<E>, 'as'>`, which it won't prove identical here.
      // Callers stay type-checked through the generic signature asserted below.
      {...(restProps as React.ComponentProps<typeof BaseButton>)}
    >
      {children}
    </BaseButton>
  )
}) as never) as unknown as <E extends React.ElementType = DefaultElement>(
  props: ButtonProps<E>,
) => React.ReactElement | null
